/**
 * `/steuerung/:id`: the teacher's phone during the game. A slim session
 * bar (phase, PIN, start, lock, end), the module's teacher view, and the
 * participant list for formats with student devices.
 */
import type { SessionStatePayload } from '@schulspiele/protocol';

import { createLifetime } from '@shared/lifetime';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { renderChips, setCount } from '@shared/ui/chips';
import { toast, toastError } from '@shared/ui/toast';
import { mountGameView } from '@shared/view-host';
import { SessionClient } from '@shared/ws-client';
import { loadGameClient } from '../../games';
import { connectionBanner, workLayout } from '../layout';
import { currentTeacher, phasePill, renderLogin } from './common';
import {
  confirmEnd,
  goneCard,
  handleChipClick,
  kickOffline,
  updateKickOffline,
} from './session-page';

export function renderControl(mount: HTMLElement, sessionId: string): () => void {
  let client: SessionClient | null = null;
  let unmountGame: (() => void) | null = null;
  const life = createLifetime();

  void (async () => {
    const teacher = await currentTeacher();
    if (life.isDisposed()) return;
    if (teacher === null) {
      // Log in here and come back to this Steuerung.
      renderLogin(mount, () => {
        renderControl(mount, sessionId);
      });
      return;
    }
    const main = workLayout(
      mount,
      `<div data-banner></div>
       <div class="card control-bar">
         <div class="control-bar__head">
           <strong data-game></strong>
           <span data-phase></span>
         </div>
         <div class="actions control-bar__actions">
           <button class="btn btn--small" data-start type="button" disabled>${t('session.start')}</button>
           <button class="btn btn--small btn--ghost" data-lock type="button" hidden></button>
           <a class="btn btn--small btn--ghost" href="/lehrer/session/${esc(sessionId)}" data-link data-details>${t('session.details')}</a>
           <button class="btn btn--small btn--danger" data-end type="button" disabled>${t('session.end')}</button>
         </div>
       </div>
       <div class="muted-box stack--tight" style="justify-items:center" data-ended hidden>
         <h2>${t('session.endedNote')}</h2>
         <a class="btn" href="/lehrer" data-link>${t('session.toTeacher')}</a>
       </div>
       <div data-game-view></div>
       <section class="card stack--tight" data-students hidden style="margin-top:var(--space-6)">
         <div class="section__head" style="margin:0">
           <h2 style="margin:0" data-count></h2>
           <button class="btn btn--small btn--ghost" data-kick-offline type="button" hidden>${t('session.kickOffline')}</button>
         </div>
         <div class="names" data-names></div>
       </section>`,
      { teacherName: teacher.displayName },
    );

    client = new SessionClient({ kind: 'teacher', sessionId });
    const c = client;
    let gone = false;
    const isGone = (): boolean => gone;
    c.on('status', (s) => {
      if (!gone) qs(main, '[data-banner]').innerHTML = connectionBanner(s);
    });
    // The game is gone (U19: after a server restart the control looked as
    // if it still ran): no stale question, timer or buttons, just the way back.
    c.on('refused', (e) => {
      gone = true;
      unmountGame?.();
      unmountGame = null;
      main.innerHTML = goneCard(e);
    });
    c.on('error', (e) => {
      if (!gone) toast(e.reason ?? t('error.generic'), 'error');
    });
    c.on('state', (s) => {
      void onState(s);
    });
    c.connect();

    let locked = false;
    qs(main, '[data-start]').addEventListener('click', () => {
      c.command('session/start').catch(toastError);
    });
    qs(main, '[data-lock]').addEventListener('click', () => {
      c.command('session/lock', { locked: !locked }).catch(toastError);
    });
    // "Beenden" first lets the game show its ranking on the board; once the
    // game is over the same button reads "Schließen" and closes for all.
    let phase: SessionStatePayload['phase'] = 'lobby';
    let over = false;
    let endedByBar = false;
    const endButton = qs<HTMLButtonElement>(main, '[data-end]');
    endButton.addEventListener('click', () => {
      confirmEnd(c, endButton, phase, over, () => {
        endedByBar = true;
      });
    });
    const kickOfflineButton = qs<HTMLButtonElement>(main, '[data-kick-offline]');
    kickOfflineButton.addEventListener('click', () => {
      kickOffline(c, kickOfflineButton);
    });
    qs(main, '[data-names]').addEventListener('click', (e) => {
      void handleChipClick(e, c);
    });

    let mounted = false;
    let wasGameOver = false;
    async function onState(s: SessionStatePayload): Promise<void> {
      if (gone) return;
      locked = s.locked;
      qs(main, '[data-game]').textContent =
        `${s.gameName}${s.needsStudentDevices && s.pin !== undefined && s.phase !== 'ended' ? ` · PIN ${s.pin}` : ''}`;
      qs(main, '[data-phase]').innerHTML = phasePill(s.phase, s.gameOver);
      // Buttons start disabled and only come alive once the server has
      // answered, so an early click cannot get lost.
      const startButton = qs<HTMLButtonElement>(main, '[data-start]');
      startButton.hidden = s.phase !== 'lobby';
      startButton.disabled = s.phase !== 'lobby';
      phase = s.phase;
      over = s.gameOver && s.phase !== 'ended';
      endButton.disabled = s.phase === 'ended';
      endButton.hidden = s.phase === 'ended';
      endButton.textContent = t(over ? 'session.close' : 'session.end');
      // Closed: say so, with the way back (U19, the control was a dead end).
      qs(main, '[data-ended]').hidden = s.phase !== 'ended';
      const lock = qs<HTMLButtonElement>(main, '[data-lock]');
      lock.hidden = !s.needsStudentDevices || s.phase === 'ended';
      // Without phones the session page only holds the board link.
      qs(main, '[data-details]').textContent = s.needsStudentDevices
        ? t('session.details')
        : t('session.boardLink');
      lock.textContent = locked ? t('session.unlock') : t('session.lock');
      const students = qs(main, '[data-students]');
      students.hidden = !s.needsStudentDevices;
      const participants = s.participants ?? [];
      setCount(qs(main, '[data-count]'), t('session.participants', { count: participants.length }));
      // After the end nobody needs renaming or removing.
      renderChips(qs(main, '[data-names]'), participants, {
        controls: s.phase !== 'ended' && !s.gameOver,
        ended: s.phase === 'ended',
      });
      updateKickOffline(kickOfflineButton, participants, s.phase);
      // The game came to its end by itself (the bar's "Beenden" needs no toast).
      if (s.gameOver && !wasGameOver && s.phase !== 'ended' && !endedByBar)
        toast(t('session.gameOver'));
      wasGameOver = s.gameOver;

      if (!mounted) {
        mounted = true;
        const game = await loadGameClient(s.gameId);
        // `gone` may have changed during the await (the refusal handler).
        if (game !== null && !life.isDisposed() && !isGone()) {
          unmountGame = mountGameView(
            game.teacher,
            qs(main, '[data-game-view]'),
            c,
            'teacher',
            teacher?.id ?? '',
          );
        }
      }
    }
  })();

  return () => {
    life.dispose();
    unmountGame?.();
    client?.close();
  };
}
